"use client";

import type { ChangeEvent, FC, ReactNode, TextareaHTMLAttributes } from "react";
import { useState } from "react";
import { cn } from "@/lib/cn";
import { FieldLabel, FieldMessage, type FieldState } from "./field-chrome";
import { countWords } from "./word-count";

type WordLimits = { min?: number; soft?: number; max?: number };

type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "required"> & {
  label?: ReactNode;
  message?: ReactNode;
  state?: FieldState;
  required?: boolean;
  words?: WordLimits;
};

export const Textarea: FC<TextareaProps> = ({
  className,
  label,
  message,
  state = "default",
  required,
  words,
  id,
  value,
  defaultValue,
  onChange,
  ...props
}) => {
  const [internal, setInternal] = useState(typeof defaultValue === "string" ? defaultValue : "");
  const current = typeof value === "string" ? value : internal;
  const count = countWords(current);
  const overMax = words?.max !== undefined && count > words.max;
  const belowSoft = words?.soft !== undefined && count < words.soft;

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (value === undefined) setInternal(event.target.value);
    onChange?.(event);
  };

  const effectiveState: FieldState = state === "error" || overMax ? "error" : "default";

  return (
    <div className="flex w-full flex-col gap-8">
      <FieldLabel htmlFor={id} state={effectiveState} required={required}>
        {label}
      </FieldLabel>
      <textarea
        id={id}
        required={required}
        value={value}
        defaultValue={defaultValue}
        onChange={handleChange}
        className={cn(
          "min-h-168 w-full resize-y rounded-[8px] border bg-neutral-white p-12 text-16 text-neutral-black outline-none placeholder:text-neutral-black/35 focus:border-brand-blue",
          effectiveState === "error" ? "border-brand-red" : "border-neutral-light-grey",
          className,
        )}
        {...props}
      />
      <div className="flex items-start justify-between gap-16">
        <FieldMessage state={effectiveState}>{message}</FieldMessage>
        {words && (
          <span
            className={cn(
              "shrink-0 text-14",
              overMax ? "text-brand-red" : belowSoft ? "text-warning" : "text-neutral-grey",
            )}
          >
            {count}
            {words.max ? `/${words.max}` : ""} words
          </span>
        )}
      </div>
    </div>
  );
};
